import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, StripeElementsOptions, Stripe } from "@stripe/stripe-js";
import { useState, createContext, ReactNode, useEffect } from "react";
import { InvoiceModel } from "../../../data/entities";
import { PaymentGatewayType } from "../../../models";
import { LocationService } from "../../../services";

const PaymentProcessorContext = createContext<PaymentGatewayType | null>(null);

interface PaymentProviderProps {
  paymentGateway: PaymentGatewayType;
  invoice: InvoiceModel;
  clientSecret?: StripeElementsOptions | undefined;
  children?: ReactNode;
}

type PaymentConfig = {
  account: string;
  publicKey: string;
  currency: string;
  publishableKey: string;
};

export const PaymentProvider: React.FC<PaymentProviderProps> = ({
  paymentGateway,
  invoice,
  children,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [options, setOptions] = useState<StripeElementsOptions | undefined>(undefined);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  const locationService = new LocationService();

  useEffect(() => {
    const fetchPaymentOptions = async () => {
      try {
        if (paymentGateway === PaymentGatewayType.Stripe) {
          const paymentOptions = await locationService.getPublicPaymentOptionsAsync(invoice.locationId ?? 0);
          const configObj = typeof paymentOptions === "string" ? JSON.parse(paymentOptions) : paymentOptions;

          const config = configObj as Partial<PaymentConfig>;
          const publishableKey = config.publishableKey || "";

          if (!publishableKey) {
            console.error("Stripe publishable key is missing — cannot initialize Stripe.");
            setLoading(false);
            return;
          }

          const stripe = loadStripe(publishableKey);
          setStripePromise(stripe);

          // Customize as needed
          setOptions({
            mode: "setup", 
            currency: config.currency || "cad",
          });
        }
      } catch (err) {
        console.error("Failed to fetch payment options:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentOptions();
  }, [paymentGateway, invoice.locationId]);

  // Stripe wrapper
  if (paymentGateway === PaymentGatewayType.Stripe && stripePromise && options) {
    return (
      <PaymentProcessorContext.Provider value={null}>
        <Elements stripe={stripePromise} options={options}>
          {children}
        </Elements>
      </PaymentProcessorContext.Provider>
    );
  }

  // Default wrapper (other gateways)
  return (
    <PaymentProcessorContext.Provider value={null}>
      {children}
    </PaymentProcessorContext.Provider>
  );
};
